/* eslint-disable react/no-danger */
/* eslint-disable no-restricted-globals */
import { useLocalization, useLocalizationState } from '@js/hooks/useLocalization';
import { h } from 'preact';
import { useMemo, useRef, useState, useCallback, useEffect } from 'preact/hooks';
import { postNewsletterSubscribe } from '../account/api';

/**
 * Opens the newsletter signup dialog after 5 seconds on the
 * product and collection pages if the user has not dismissed it in the last week.
 *
 * @param {HTMLElement} dialogElement - The dialog element to be displayed.
 */
const signupNag = (dialogElement) => {
  const popupButton = document.querySelector('button[data-newsletter-trigger]');
  const dismissedTime = localStorage.getItem('newsletter-dismissed');
  const ONE_WEEK = 604800000;

  if (dialogElement && popupButton) {
    if (
      (!dismissedTime || Date.now() - dismissedTime > ONE_WEEK) &&
      location.pathname.match(/(\/products\/|\/collections\/)/)
    ) {
      setTimeout(() => dialogElement.showModal(), 5000);
    }
  }
};

function Subscribe({ image, setSubscribed }) {
  const localization = useLocalizationState();
  const { t } = useLocalization('getLocale', localization);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    const formData = new FormData(form);

    const interests = [];

    formData.forEach((value, key) => {
      if (key === 'interests') {
        interests.push(value);
      }
    });

    if (interests.length === 0) {
      setError(t('marketing.newsletter.popup.register.options.error'));
      return;
    }

    setSubmitting(true);
    setError(null);

    const responseJson = await postNewsletterSubscribe(window.SlrsIdentify.email, interests);

    setSubmitting(false);

    if (responseJson.message === 'success') {
      document.querySelector('#newsletter').classList.add('tw-hidden');
      document.querySelector('#newsletter-subscribed').classList.remove('tw-hidden');
      document.querySelectorAll('button[data-newsletter-trigger]').forEach((el) => el.classList.add('tw-hidden'));
      setSubscribed(true);
    }
  };

  return (
    <div class="tw-w-full tw-max-w-[500px]">
      <img alt="newsletter_image" class="tw-w-full tw-object-fill" loading="lazy" src={image} />
      <section class="tw-w-full tw-rounded-bl tw-rounded-br-[3rem] tw-bg-white">
        <div class="tw-px-16 tw-py-8 xs:tw-px-8 xs:tw-py-4">
          <div class="tw-mb-8 xs:tw-mb-4">
            <h2 class="tw-m-0 tw-mb-3 tw-p-0 tw-text-center tw-text-2xl tw-font-bold tw-text-black xs:tw-text-lg">
              {window.SlrsIdentify?.accepts_marketing ?
                t('marketing.newsletter.popup.register.header_existing') :
                t('marketing.newsletter.popup.register.header_new')}
            </h2>

            <p
              class="tw-m-0 tw-p-0 tw-px-12 tw-text-center tw-text-gray-500 xs:tw-px-2 xs:tw-text-sm"
              style={{ fontSize: '17px' }}
            >
              {t('marketing.newsletter.popup.register.content')}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <fieldset class="tw-mb-8 tw-flex tw-flex-col tw-gap-3">
              <div>
                <label class="filter__checkbox" style={{ fontSize: '17px' }}>
                  {t('marketing.newsletter.popup.register.options.newsletter')}
                  <input name="interests" type="checkbox" value="Newsletter" />
                  <span class="checkmark" />
                </label>
              </div>

              <div>
                <label class="filter__checkbox" style={{ fontSize: '17px' }}>
                  {t('marketing.newsletter.popup.register.options.10for10')}
                  <input name="interests" type="checkbox" value="10for10" />
                  <span class="checkmark" />
                </label>
              </div>
            </fieldset>

            {error && (
              <p class="tw-m-0 tw-mb-2 tw-p-0 tw-text-center tw-text-red-500 xs:tw-px-4" style={{ fontSize: '13px' }}>
                {error}
              </p>
            )}

            <button
              disabled={submitting}
              type="submit"
              class={`tw-grid tw-w-full tw-place-items-center ${submitting ? 'tw-bg-gray-300' : 'tw-bg-[#EE404E]'} 
                tw-h-[50px] tw-max-h-[50px] tw-rounded-[4px] tw-font-medium tw-text-white xs:tw-max-h-[40px]`}
            >
              {t('marketing.newsletter.popup.register.button')}
            </button>

            <p class="tw-m-0 tw-mt-2 tw-p-0 tw-text-center tw-text-gray-500 xs:tw-px-4" style={{ fontSize: '13px' }}>
              {t('marketing.newsletter.popup.register.footer')}
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Success({ image, closeModal }) {
  const localization = useLocalizationState();
  const { t } = useLocalization('getLocale', localization);

  return (
    <div class="tw-w-full tw-max-w-[500px]">
      <img alt="newsletter_image" class="tw-w-full tw-object-fill" loading="lazy" src={image} />

      <section class="tw-w-fill tw-rounded-bl tw-rounded-br-[3rem] tw-bg-white">
        <div class="tw-px-16 tw-py-8 xs:tw-px-8 xs:tw-py-4">
          <div class="tw-grid tw-place-items-center">
            <svg
              class="tw-h-[40px] tw-w-[40px] xs:tw-h-[32px] xs:tw-w-[32px] sm:tw-h-[36px] sm:tw-w-[36px]"
              fill="none"
              viewBox="0 0 40 40"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M35.8333 30L24.7619 20M15.2381 20L4.16672 30M3.33333 11.6667L16.9415 21.1924C18.0435 21.9638 18.5945 22.3495 19.1938 22.4989C19.7232 22.6308 20.2768 22.6308 20.8062 22.4989C21.4055 22.3495 21.9565 21.9638 23.0585 21.1924L26.4605 18.811M22.5 6.66667H11.3333C8.53307 6.66667 7.13293 6.66667 6.06338 7.21164C5.12257 7.69101 4.35766 8.45591 3.8783 9.39672C3.33333 10.4663 3.33333 11.8664 3.33333 14.6667V25.3333C3.33333 28.1336 3.33333 29.5337 3.8783 30.6033C4.35766 31.5441 5.12257 32.309 6.06338 32.7884C7.13293 33.3333 8.53307 33.3333 11.3333 33.3333H28.6667C31.4669 33.3333 32.8671 33.3333 33.9366 32.7884C34.8774 32.309 35.6423 31.5441 36.1217 30.6033C36.6667 29.5337 36.6667 28.1336 36.6667 25.3333V21.1924"
                opacity="0.26"
                stroke="black"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
              />
              <path
                d="M36.8683 6.46447C38.8209 8.41709 38.8209 11.5829 36.8683 13.5355C34.9156 15.4882 31.7498 15.4882 29.7972 13.5355C27.8446 11.5829 27.8446 8.41709 29.7972 6.46447C31.7498 4.51184 34.9156 4.51184 36.8683 6.46447Z"
                stroke="#EE404E"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
              />
            </svg>
          </div>

          <div class="tw-mb-6">
            <h2 class="tw-m-0 tw-mb-3 tw-mt-2 tw-p-0 tw-text-center tw-text-2xl tw-font-bold tw-text-black xs:tw-text-lg">
              {t('marketing.newsletter.popup.success.header')}
            </h2>

            <p class="tw-m-0 tw-p-0 tw-px-2 tw-text-center tw-text-gray-500 xs:tw-text-sm" style={{ fontSize: '17px' }}>
              {window.SlrsIdentify?.accepts_marketing ?
                t('marketing.newsletter.popup.success.content_already_subscribed') :
                t('marketing.newsletter.popup.success.content_just_subscribed')}
            </p>
          </div>

          <button
            class="tw-grid tw-h-[50px] tw-max-h-[50px] tw-w-full tw-place-items-center tw-rounded-[4px] tw-bg-[#EE404E] tw-font-medium tw-text-white xs:tw-max-h-[40px]"
            style={{ fontSize: '17px' }}
            onClick={closeModal}
          >
            {t('marketing.newsletter.popup.success.button')}
          </button>
        </div>
      </section>
    </div>
  );
}

const getImages = () => {
  const { images } = JSON.parse(document.querySelector('#getImages').textContent);
  return images;
};

function Newsletter() {
  const [subscribed, setSubscribed] = useState(false);
  const images = useMemo(getImages, []);
  const dialog = useRef(null);

  useEffect(() => {
    signupNag(dialog.current);
  }, [dialog]);

  const closeModal = useCallback(() => {
    dialog.current.close();
  }, [dialog]);

  const handleClose = useCallback(() => {
    localStorage.setItem('newsletter-dismissed', Date.now());
    setSubscribed(false);
  }, []);

  return (
    <dialog
      ref={dialog}
      class="tw-m-0 tw-grid tw-h-[100vh] tw-max-h-[unset] tw-w-[100vw] tw-max-w-[unset] tw-place-items-center tw-bg-transparent tw-p-0"
      id="newsletter-modal"
      style={{
        overflow: 'clip',
        backdropFilter: 'blur(2px)',
        backgroundColor: 'rgba(246, 249, 252, 0.5)',
      }}
      onClose={handleClose}
    >
      <div class="tw-relative tw-m-4 sm:tw-mb-16" id="modal-content">
        <button
          class="tw-absolute tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-full tw-bg-gray-400 sm:tw-bottom-[-48px] sm:tw-right-[calc(50%-18px)] md:tw-right-0 md:tw-top-[9%]"
          onClick={closeModal}
        >
          <svg fill="none" height="14" viewBox="0 0 14 14" width="14" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M13 1L1 13M1 1L13 13"
              stroke="white"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            />
          </svg>
        </button>

        {subscribed ? (
          <Success closeModal={closeModal} image={images.newsletter_success} />
        ) : (
          <Subscribe image={images.newsletter_register} setSubscribed={setSubscribed} />
        )}
      </div>
    </dialog>
  );
}

export default Newsletter;
